/**
 * Stripe Service
 * Handles subscription checkout and management
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const stripeService = {
  /**
   * Create a Stripe checkout session
   * @param {string} plan - Plan ID (free, starter, pro, scale, enterprise)
   * @param {string} billingCycle - 'monthly' or 'annual'
   * @param {string[]|null} addons - Optional array of addon IDs (ci_proof_pro, signals_api, market_signals)
   * @param {string|null} email - Customer email (optional)
   * @param {string|null} merchantId - Merchant ID — REQUIRED for the webhook to
   *   link the payment to the account; falls back to the phone-signup id.
   */
  async createCheckoutSession(plan, billingCycle = 'monthly', addons = null, email = null, merchantId = null) {
    try {
      // Convert addons to array if passed as comma-separated string (backwards compatibility)
      let addonsList = null;
      if (addons) {
        if (typeof addons === 'string') {
          addonsList = addons.split(',').filter(a => a.trim());
        } else if (Array.isArray(addons)) {
          addonsList = addons.filter(a => a.trim());
        }
      }

      // Without a merchant_id the webhook cannot upgrade the account
      const resolvedMerchantId = merchantId
        || localStorage.getItem('ci_merchant_id')
        || sessionStorage.getItem('merchant_id')
        || null;

      const response = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // The backend binds the checkout to the session merchant; anonymous signup checkout sends no token.
          ...((sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token'))
            ? { Authorization: `Bearer ${sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token')}` }
            : {}),
        },
        body: JSON.stringify({
          plan: plan,
          billing_cycle: billingCycle,
          addons: addonsList,
          merchant_id: resolvedMerchantId,
          success_url: `${window.location.origin}/checkout/success?plan=${plan}&billing=${billingCycle}${addonsList ? `&addons=${addonsList.join(',')}` : ''}`,
          cancel_url: `${window.location.origin}/checkout?cancelled=true&plan=${plan}`,
          email: email,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (data && data.claim_token) {
        this.rememberPendingCheckout(data.session_id, data.claim_token);
      }
      return data;
    } catch (error) {
      console.error('Stripe checkout error:', error);
      throw error;
    }
  },

  /**
   * Get current subscription status
   */
  /**
   * Public-signup checkout: remember {session id, claim token} in this browser at creation time. The token is returned
   * only to the caller that created the session, so a session id seen in a redirect or log cannot claim. After sign-in,
   * claimPendingCheckout attaches the paid session to the signed-in merchant (the backend verifies with Stripe).
   */
  rememberPendingCheckout(sessionId, claimToken) {
    if (sessionId && claimToken && String(sessionId).startsWith('cs_')) {
      localStorage.setItem('ci_pending_checkout', JSON.stringify({ session_id: sessionId, claim_token: claimToken }));
    }
  },

  /** Server-verified payment status of the pending public checkout: 'paid' | 'unpaid' | 'unknown' | 'none'. Never throws. */
  async pendingCheckoutStatus() {
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem('ci_pending_checkout') || 'null'); } catch (e) { pending = null; }
    if (!pending || !pending.session_id || !pending.claim_token) return 'none';
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/checkout-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pending),
      });
      if (!response.ok) return 'unknown';
      const body = await response.json();
      return body.state === 'paid' || body.state === 'unpaid' ? body.state : 'unknown';
    } catch (e) {
      return 'unknown';
    }
  },

  /** True when this browser holds a paid public checkout that has not been linked to an account yet. */
  hasPendingCheckout() {
    try {
      const p = JSON.parse(localStorage.getItem('ci_pending_checkout') || 'null');
      return !!(p && p.session_id && p.claim_token);
    } catch (e) { return false; }
  },

  /**
   * Claim the remembered paid checkout for the signed-in merchant. Never throws. Returns
   *   { state: 'none' }                    nothing pending in this browser
   *   { state: 'signed_out' }              pending, but no session token yet
   *   { state: 'claimed', plan }           linked to this account
   *   { state: 'retry', reason }           recoverable (401, 5xx, provider unavailable, payment not complete yet,
   *                                         network): the claim stays stored for the next attempt
   *   { state: 'failed', code }            terminal (unknown, linked to another account, invalid plan): claim cleared
   */
  async claimPendingCheckout() {
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem('ci_pending_checkout') || 'null'); } catch (e) { pending = null; }
    if (!pending || !pending.session_id || !pending.claim_token) return { state: 'none' };
    const token = sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token');
    if (!token) return { state: 'signed_out' };
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/claim-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(pending),
      });
      if (response.ok) {
        const body = await response.json();
        localStorage.removeItem('ci_pending_checkout');
        return { state: 'claimed', plan: body.plan };
      }
      let code = '';
      try { const b = await response.json(); code = (b && b.detail && b.detail.code) || ''; } catch (e) { code = ''; }
      const recoverable = response.status === 401 || response.status >= 500 || code === 'not_complete';
      if (recoverable) return { state: 'retry', reason: code || String(response.status) };
      localStorage.removeItem('ci_pending_checkout');
      return { state: 'failed', code: code || String(response.status) };
    } catch (e) {
      return { state: 'retry', reason: 'network' };
    }
  },

  async getSubscriptionStatus(merchantId) {
    try {
      const token = sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BACKEND_URL}/api/billing/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No subscription
        }
        throw new Error('Failed to fetch subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Get subscription error:', error);
      return null;
    }
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    try {
      const token = sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BACKEND_URL}/api/billing/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Cancel subscription error:', error);
      throw error;
    }
  },

  /**
   * Get customer portal URL
   */
  async getPortalUrl() {
    try {
      const token = sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BACKEND_URL}/api/billing/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          return_url: window.location.origin + '/dashboard',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get portal URL');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Get portal URL error:', error);
      throw error;
    }
  },
};
